import {QuoteDetailsApiDto} from "../../api/model/rest";
import {none, option, Option} from "ts-option";
import Amount from "../common/Amount";
import QuoteItem from "./QuoteItem";
import Attachment from "../attachment/Attachment";
import * as _ from "underscore";
import moment = require("moment");

export default class QuoteDetails {
	constructor(public readonly id: string,
		public readonly emailContent: Option<string>,
		public readonly items: QuoteItem[],
		public readonly totalPrice: Amount,
		public readonly referenceNumber: Option<string>,
		public readonly markupAmount: Amount,
		public readonly hiberCommission: Option<Amount>,
		public readonly installerGross: Option<Amount>,
		public readonly installerBonusAmount: Option<Amount>,
		public readonly attachments: Attachment[],
		public readonly quoteDate: moment.Moment,
		public readonly quoteStatus: QuoteStatusEnum,
		public readonly totalPriceBeforeVat: Amount,
		public readonly vatAmount: Option<Amount>,
		public readonly vatRegistered: boolean,
		public readonly labourTotalCost: Amount,
		public readonly vat: number,
		public readonly sentToCustomer: boolean,
		public readonly rewardPoints: Option<number>,
		public readonly payByFinance: boolean,
		public readonly financeProductId: Option<string>,
		public readonly versionNumber: number) {
	}

	static fromJson(json: QuoteDetailsApiDto): QuoteDetails {
		const items = json.items.map(QuoteItem.fromJson);
		const attachments = json.attachments.map(Attachment.fromJson);
		return new QuoteDetails(
			json.id,
			option(json.emailContent),
			items,
			new Amount(json.totalPrice),
			option(json.referenceNumber),
			new Amount(json.markupAmount),
			json.hiberCommission ? option(new Amount(json.hiberCommission)) : none,
			json.installerGross ? option(new Amount(json.installerGross)) : none,
			json.installerBonusAmount ? option(new Amount(json.installerBonusAmount)) : none,
			attachments,
			moment(json.quoteDate),
			this.getStatus(json.quoteStatus),
			new Amount(json.totalPriceBeforeVat),
			json.vatAmount ? option(new Amount(json.vatAmount)) : none,
			json.vatRegistered,
			new Amount(json.labourTotalCost),
			json.vat,
			json.sentToCustomer,
			option(json.rewardPoints),
			json.payByFinance,
			option(json.financeProductId),
			json.numberVersion
		)
	}

	static getStatus(status: string) {
		switch (status) {
			case QuoteStatusEnum.Accepted:
				return QuoteStatusEnum.Accepted;
			case QuoteStatusEnum.Declined:
				return QuoteStatusEnum.Declined;
			default:
				return QuoteStatusEnum.New;
		}
	}

	/**
	 * If false, quote has at least one asset.
	 */
	hasNoAssets() {
		return !this.items.length;
	}

	getHiberCommision(): Amount {
		return this.hiberCommission.getOrElse(new Amount(0));
	}

	getPartsPrice() {
		return this.items.reduce((sum, item) => sum.add(
			item.invoiceLineItem ? Amount.fromPounds(0) : (this.vatRegistered ? item.totalPrice : item.totalPriceWithVat)),
			Amount.fromPounds(0))
	}

	getInstallerGross(): Amount {
		const labourItemsPrice = new Amount(
			_.reduce(this.items, (memo, item) => memo + (item.invoiceLineItem ? item.totalPrice.cents : 0), 0));
		return this.installerGross.getOrElse(new Amount(0)).add(labourItemsPrice);
	}

	copy({financeProductId = this.financeProductId}) {
		return new QuoteDetails(this.id, this.emailContent, this.items, this.totalPrice, this.referenceNumber, this.markupAmount,
			this.hiberCommission, this.installerGross, this.installerBonusAmount, this.attachments, this.quoteDate,
			this.quoteStatus, this.totalPriceBeforeVat, this.vatAmount, this.vatRegistered, this.labourTotalCost, this.vat,
			this.sentToCustomer, this.rewardPoints, this.payByFinance, financeProductId, this.versionNumber)
	}
}

export enum QuoteStatusEnum {
	New = "NEW",
	Accepted = "ACCEPTED",
	Declined = "DECLINED"
}
